import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, ContentSection, Heading, Paragraph } from '@vwfs-bronson/bronson-react';
import { assertDefined } from '../../services/common/miscUtils';
import { getConsents, postConsents } from '../../services/consent';
import { staticSelector, store } from '../../services/redux';
import { setConsents, updateConsent } from '../../services/redux/features/consents.slice';
import { creditApplicationSelectors } from '../../services/redux/features/creditApplication.slice';
import { customerSelectors } from '../../services/redux/features/customer.slice';
import { PostConsentsRequest } from '../../types/consent';

export const getConsentsRequestCustomerData = (
  customerType: CreditIntegration.Schemas.CustomerType,
  isCoCustomer: boolean | undefined,
  isSoleTrader: boolean | undefined
): PostConsentsRequest['customerData'] => {
  const creditApplicantCustomerData = staticSelector(creditApplicationSelectors.selectCreditApplicantCustomerData);

  if (customerType === 'INDIVIDUAL') {
    let _ = creditApplicantCustomerData as CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse;

    if (isCoCustomer) {
      _ = staticSelector(
        creditApplicationSelectors.selectCreditApplicantCustomerDataCoCustomer
      ) as CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse;
    }

    return {
      lastName: _.lastName!,
      customerType,
      contactData: {
        ..._.contactData,
        emails: [
          {
            ..._.contactData!.emails![0],
            type: 'PERSONAL_PRIMARY',
          },
        ],
      },
    };
  }

  if (isSoleTrader) {
    const _ = creditApplicantCustomerData as CreditIntegration.Schemas.UpdateSoleTraderCustomerDataResponse;
    return {
      lastName: _.customerRelations![0].lastName!,
      customerType,
      contactData: {
        ..._.contactData,
        emails: [
          {
            ..._.contactData!.emails![0],
            type: 'COMPANY_PRIMARY',
          },
        ],
      },
    };
  }

  const _ = creditApplicantCustomerData as CreditIntegration.Schemas.UpdateLegalEntityCustomerDataResponse;
  const stakeholderContacts = staticSelector(customerSelectors.selectStakeholderContacts);
  const executiveStakeholder = stakeholderContacts.DIRECTOR;

  return {
    customerType,
    lastName: assertDefined(
      executiveStakeholder?.formData.ceoLastName,
      'Missing Executive stakeholder data, consent(s) cannot be submitted.'
    ),
    contactData: {
      ..._.contactData,
      emails: [
        {
          ..._.contactData!.emails![0],
          type: 'COMPANY_PRIMARY',
        },
      ],
    },
  };
};

export const submitConsents = async <T extends PostConsentsRequest['customerData']>(customerData: T) => {
  const { consents } = store.getState().consents;

  const payload: PostConsentsRequest = {
    consentsData: consents?.map(({ id, isChecked }) => {
      return {
        consentId: id,
        isChecked,
      };
    }),
    customerData: {
      customerType: customerData.customerType,
      contactData: customerData.contactData,
      lastName: customerData.lastName,
    },
  };

  await postConsents(payload);
};

export const Consents: React.FC = () => {
  const dispatch = useDispatch();
  const { consents } = useSelector((state: { consents: any }) => state.consents);
  const { t } = useTranslation();

  const handleConsentChange = useCallback(
    (consentId: string, isChecked: boolean) => {
      dispatch(updateConsent({ consentId, isChecked }));
    },
    [dispatch]
  );

  useEffect(() => {
    const getConsentsData = async () => {
      try {
        const data = await getConsents();
        const consentData = data.map((consent) => {
          return {
            ...consent.customerData.consentData[0],
            isChecked: false,
          };
        });
        dispatch(setConsents(consentData));
      } catch (err) {
        return [];
      }
      return [];
    };

    getConsentsData();
  }, [dispatch]);

  if (!consents || consents.length <= 0) {
    return null;
  }

  const consentTeachings = () => {
    const link =
      '<a href="https://www.vwfs.fr/espace-client/Optionals-formulaires/politique-donnees-personnelles.html">vwfs.fr</a>';
    const html = t('consents:teachings:vwbankPartners', { link });
    return (
      <>
        <Paragraph className="u-font-size-fs-2">{t('consents:teachings:vwbank')}</Paragraph>
        <Paragraph className="u-font-size-fs-2" dangerouslySetInnerHTML={{ __html: html }} />
      </>
    );
  };
  const consentHeading = () => {
    const br = '</br>';
    const html = t('consents:heading', { br });
    return <Heading level={6} dangerouslySetInnerHTML={{ __html: html }} />;
  };

  return (
    <ContentSection className="u-pv-small u-mv-md">
      <div className="u-pl-large u-pl-small@s u-pt">{consentHeading()}</div>

      {consents?.map((consent) => (
        <Checkbox
          key={consent.id}
          className="u-m-small"
          name={consent.consentTypeDisplayLabel}
          checked={consent.isChecked}
          onChange={(e) => handleConsentChange(consent.id, e.target.checked)}
        >
          {consent.description}
        </Checkbox>
      ))}
      <div className="u-m-small u-mt">{consentTeachings()}</div>
    </ContentSection>
  );
};
